const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const SERVICE_WORKER_PATH = "/service-worker.js";

function getToken() {
  return localStorage.getItem("token");
}

async function apiRequest(path, options = {}) {
  const response = await fetch(`${API_BASE_URL}/push-notifications${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getToken()}`,
      ...(options.headers || {}),
    },
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data.message || "Não foi possível concluir esta ação.");
    error.status = response.status;
    error.code = data.code;
    throw error;
  }
  return data;
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = `${base64String}${padding}`.replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((character) => character.charCodeAt(0)));
}

export function isIosDevice() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent || "");
}

export function isStandaloneApp() {
  return Boolean(
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
      window.navigator.standalone === true
  );
}

export function supportsWebPush() {
  return Boolean(
    "serviceWorker" in navigator &&
      "PushManager" in window &&
      "Notification" in window
  );
}

function getPlatform() {
  const userAgent = navigator.userAgent || "";
  if (/iphone|ipad|ipod/i.test(userAgent)) return "ios";
  if (/android/i.test(userAgent)) return "android";
  if (/macintosh|mac os x/i.test(userAgent)) return "macos";
  if (/windows/i.test(userAgent)) return "windows";
  if (/linux/i.test(userAgent)) return "linux";
  return "unknown";
}

function getBrowserName() {
  const userAgent = navigator.userAgent || "";
  if (/edg/i.test(userAgent)) return "Edge";
  if (/firefox|fxios/i.test(userAgent)) return "Firefox";
  if (/chrome|crios/i.test(userAgent)) return "Chrome";
  if (/safari/i.test(userAgent)) return "Safari";
  return "Navegador";
}

function getDeviceLabel() {
  const labels = {
    ios: "iPhone/iPad",
    android: "Android",
    macos: "Mac",
    windows: "Windows",
    linux: "Linux",
    unknown: "Dispositivo",
  };
  return `${labels[getPlatform()]} · ${getBrowserName()}`;
}

async function getRegistration({ create = false } = {}) {
  let registration = await navigator.serviceWorker.getRegistration("/");
  if (!registration && create) {
    registration = await navigator.serviceWorker.register(SERVICE_WORKER_PATH, { scope: "/" });
  }
  if (registration && !registration.active) {
    registration = await navigator.serviceWorker.ready;
  }
  return registration;
}

async function saveSubscription(subscription) {
  const json = subscription.toJSON();
  await apiRequest("/subscribe", {
    method: "POST",
    body: JSON.stringify({
      subscription: json,
      metadata: {
        deviceLabel: getDeviceLabel(),
        platform: getPlatform(),
      },
    }),
  });
}

export async function getPushStatus() {
  if (isIosDevice() && !isStandaloneApp()) {
    return { status: "ios_install_required", label: "Instale na Tela de Início" };
  }

  if (!supportsWebPush()) {
    return { status: "unsupported", label: "Não disponível neste navegador" };
  }

  const permission = Notification.permission;
  if (permission === "denied") {
    return { status: "blocked", label: "Bloqueadas no navegador" };
  }

  if (permission !== "granted") {
    return { status: "inactive", label: "Desativadas" };
  }

  const registration = await getRegistration();
  const subscription = await registration?.pushManager?.getSubscription();
  return subscription
    ? { status: "active", label: "Ativas neste dispositivo" }
    : { status: "inactive", label: "Ativação incompleta" };
}

export async function activatePushNotifications() {
  if (isIosDevice() && !isStandaloneApp()) {
    const error = new Error(
      "No iPhone, use Compartilhar > Adicionar à Tela de Início. Depois abra o app instalado e ative os alertas."
    );
    error.code = "IOS_INSTALL_REQUIRED";
    throw error;
  }

  if (!supportsWebPush()) {
    const error = new Error("Este navegador não oferece notificações push.");
    error.code = "UNSUPPORTED";
    throw error;
  }

  const { publicKey } = await apiRequest("/public-key");
  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    const error = new Error(
      permission === "denied"
        ? "As notificações estão bloqueadas. Libere a permissão nas configurações do navegador."
        : "A permissão não foi concluída. Toque em ativar e escolha Permitir."
    );
    error.code = "PERMISSION_NOT_GRANTED";
    throw error;
  }

  const registration = await getRegistration({ create: true });
  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
  }

  await saveSubscription(subscription);
  window.dispatchEvent(new CustomEvent("futuretrade:push-status-changed"));
  return subscription;
}

export async function syncGrantedPushSubscription() {
  if (!supportsWebPush() || Notification.permission !== "granted") return null;
  if (isIosDevice() && !isStandaloneApp()) return null;

  const registration = await getRegistration({ create: true });
  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) return null;
  await saveSubscription(subscription);
  return subscription;
}

export async function deactivatePushNotifications() {
  const registration = await getRegistration();
  const subscription = await registration?.pushManager?.getSubscription();
  if (!subscription) return;

  await apiRequest("/unsubscribe", {
    method: "POST",
    body: JSON.stringify({ endpoint: subscription.endpoint }),
  });
  await subscription.unsubscribe();
  window.dispatchEvent(new CustomEvent("futuretrade:push-status-changed"));
}

export async function sendPushTest() {
  return apiRequest("/test", { method: "POST" });
}
